import React, { useState } from 'react';
import {
  createItemFormField,
  createItemFormFromPreset,
  createCustomItemForm,
  itemCurrencySymbols,
  itemFieldTypes,
  itemFormPresets,
  normaliseItemForms,
} from '../utils/itemForms';
import { normaliseOptionList } from '../utils/formOptions';

function OptionEditor({ inputId, onChange, value }) {
  const [draft, setDraft] = useState('');
  const options = normaliseOptionList(value);

  const addOption = () => {
    const nextOption = draft.trim();
    if (!nextOption) return;
    onChange([...options, nextOption]);
    setDraft('');
  };

  return (
    <div>
      <label className="field-label" htmlFor={inputId}>Options</label>
      <div className="flex gap-2">
        <input
          className="field"
          id={inputId}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addOption();
            }
          }}
          placeholder="Add option"
          value={draft}
        />
        <button
          className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!draft.trim()}
          onClick={addOption}
          type="button"
        >
          Add
        </button>
      </div>
      {options.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {options.map((option) => (
            <span className="chip inline-flex items-center gap-2" key={option}>
              {option}
              <button
                aria-label={`Remove ${option}`}
                className="text-slate-500 transition hover:text-slate-950"
                onClick={() => onChange(options.filter((item) => item !== option))}
                type="button"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemFormsBuilder({ activeGridId = '', forms = [], onChange }) {
  const itemForms = normaliseItemForms(forms);
  const visibleItemForms = activeGridId
    ? itemForms.filter((form) => (form.gridId || activeGridId) === activeGridId)
    : itemForms;

  const updateForms = (nextForms) => onChange(nextForms);

  const updateForm = (formId, changes) => {
    updateForms(itemForms.map((form) => (
      form.id === formId ? { ...form, gridId: form.gridId || activeGridId, ...changes } : form
    )));
  };

  const updateField = (formId, fieldId, changes) => {
    updateForms(itemForms.map((form) => (
      form.id === formId
        ? {
          ...form,
          gridId: form.gridId || activeGridId,
          itemFields: (form.itemFields || []).map((field) => (
            field.id === fieldId ? { ...field, ...changes } : field
          )),
        }
        : form
    )));
  };

  const addForm = (presetId) => {
    updateForms([
      ...itemForms,
      {
        ...createItemFormFromPreset(presetId, itemForms.length + 1),
        gridId: activeGridId,
      },
    ]);
  };

  const addCustomForm = () => {
    updateForms([
      ...itemForms,
      {
        ...createCustomItemForm(itemForms.length + 1),
        gridId: activeGridId,
      },
    ]);
  };

  const addField = (formId) => {
    updateForms(itemForms.map((form) => (
      form.id === formId
        ? { ...form, itemFields: [...(form.itemFields || []), createItemFormField((form.itemFields || []).length + 1)] }
        : form
    )));
  };

  const removeField = (formId, fieldId) => {
    updateForms(itemForms.map((form) => (
      form.id === formId
        ? { ...form, itemFields: (form.itemFields || []).filter((field) => field.id !== fieldId) }
        : form
    )));
  };

  const moveField = (formId, fieldIndex, direction) => {
    updateForms(itemForms.map((form) => {
      if (form.id !== formId) return form;
      const itemFields = [...(form.itemFields || [])];
      const nextIndex = fieldIndex + direction;
      if (nextIndex < 0 || nextIndex >= itemFields.length) return form;
      [itemFields[fieldIndex], itemFields[nextIndex]] = [itemFields[nextIndex], itemFields[fieldIndex]];
      return { ...form, itemFields };
    }));
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <div>
        <p className="label-caps">Internal item forms</p>
        <p className="mt-2 text-sm leading-6 text-slate-500">
          Build owner-only workflows for stock taking, bookings, and item management on the active grid.
        </p>
      </div>

      <div className="mt-5 grid gap-2">
        <button
          className="rounded bg-slate-950 px-3 py-2 text-left text-sm font-semibold text-white transition hover:bg-slate-800"
          onClick={addCustomForm}
          type="button"
        >
          Create custom item form
        </button>
        {itemFormPresets.map((preset) => (
          <button
            className="rounded border border-slate-300 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
            key={preset.id}
            onClick={() => addForm(preset.id)}
            type="button"
          >
            Add {preset.label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {visibleItemForms.map((form, formIndex) => (
          <div className="rounded border border-slate-200 bg-slate-50 p-3" key={form.id}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-slate-950">Item form {formIndex + 1}</p>
              <button
                className="text-sm font-semibold text-red-700"
                onClick={() => updateForms(itemForms.filter((item) => item.id !== form.id))}
                type="button"
              >
                Remove
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                checked={form.enabled !== false}
                onChange={(event) => updateForm(form.id, { enabled: event.target.checked })}
                type="checkbox"
              />
              Enabled
            </label>

            <label className="field-label" htmlFor={`${form.id}-title`}>Title</label>
            <input
              className="field"
              id={`${form.id}-title`}
              onChange={(event) => updateForm(form.id, { title: event.target.value })}
              value={form.title || ''}
            />

            <label className="field-label" htmlFor={`${form.id}-button-text`}>Item card button text</label>
            <input
              className="field"
              id={`${form.id}-button-text`}
              onChange={(event) => updateForm(form.id, { buttonText: event.target.value })}
              placeholder="Item form"
              value={form.buttonText || ''}
            />

            <label className="field-label" htmlFor={`${form.id}-submit-label`}>Submit button</label>
            <input
              className="field"
              id={`${form.id}-submit-label`}
              onChange={(event) => updateForm(form.id, { submitLabel: event.target.value })}
              value={form.submitLabel || ''}
            />

            <div className="mt-4 space-y-3">
              {(form.itemFields || []).map((field, fieldIndex) => (
                <div className="rounded border border-slate-200 bg-white p-3" key={field.id}>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase text-slate-500">Item field {fieldIndex + 1}</p>
                    <div className="flex items-center gap-2">
                      <button
                        aria-label={`Move ${field.label || `item field ${fieldIndex + 1}`} up`}
                        className="rounded border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                        disabled={fieldIndex === 0}
                        onClick={() => moveField(form.id, fieldIndex, -1)}
                        type="button"
                      >
                        &uarr;
                      </button>
                      <button
                        aria-label={`Move ${field.label || `item field ${fieldIndex + 1}`} down`}
                        className="rounded border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                        disabled={fieldIndex === (form.itemFields || []).length - 1}
                        onClick={() => moveField(form.id, fieldIndex, 1)}
                        type="button"
                      >
                        &darr;
                      </button>
                      <button
                        className="text-sm font-semibold text-red-700"
                        onClick={() => removeField(form.id, field.id)}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <label className="field-label" htmlFor={`${field.id}-label`}>Label</label>
                  <input
                    className="field"
                    id={`${field.id}-label`}
                    onChange={(event) => updateField(form.id, field.id, { label: event.target.value })}
                    value={field.label || ''}
                  />

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="field-label" htmlFor={`${field.id}-type`}>Type</label>
                      <select
                        className="field"
                        id={`${field.id}-type`}
                        onChange={(event) => updateField(form.id, field.id, {
                          type: event.target.value,
                          ...(event.target.value === 'currency' ? { currencySymbol: field.currencySymbol || 'R' } : {}),
                        })}
                        value={field.type || 'text'}
                      >
                        {itemFieldTypes.map((type) => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>

                    <label className="mt-7 flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <input
                        checked={Boolean(field.required)}
                        onChange={(event) => updateField(form.id, field.id, { required: event.target.checked })}
                        type="checkbox"
                      />
                      Required
                    </label>
                  </div>

                  {field.type === 'currency' && (
                    <div>
                      <label className="field-label" htmlFor={`${field.id}-currency-symbol`}>Currency symbol</label>
                      <select
                        className="field"
                        id={`${field.id}-currency-symbol`}
                        onChange={(event) => updateField(form.id, field.id, { currencySymbol: event.target.value })}
                        value={field.currencySymbol || 'R'}
                      >
                        {itemCurrencySymbols.map((symbol) => (
                          <option key={symbol} value={symbol}>{symbol}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {!['checkbox', 'select'].includes(field.type) && (
                    <>
                      <label className="field-label" htmlFor={`${field.id}-placeholder`}>Placeholder</label>
                      <input
                        className="field"
                        id={`${field.id}-placeholder`}
                        onChange={(event) => updateField(form.id, field.id, { placeholder: event.target.value })}
                        value={field.placeholder || ''}
                      />
                    </>
                  )}

                  {field.type === 'select' && (
                    <OptionEditor
                      inputId={`${field.id}-options`}
                      onChange={(options) => updateField(form.id, field.id, { options })}
                      value={field.options || []}
                    />
                  )}
                </div>
              ))}
            </div>

            <button
              className="mt-3 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
              onClick={() => addField(form.id)}
              type="button"
            >
              Add item field
            </button>
          </div>
        ))}

        {!visibleItemForms.length && (
          <p className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500">
            Create a custom form or add a preset to create the first internal item form for this grid.
          </p>
        )}
      </div>
    </section>
  );
}

export default ItemFormsBuilder;
